import { useEffect, useState } from 'react'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'

const apiBaseUrl = resolveApiBaseUrl()

type SettingsPageProps = {
  session: AdminSession
}

export function SettingsPage({ session }: SettingsPageProps) {
  const [delayTolerance, setDelayTolerance] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void fetch(`${apiBaseUrl}/settings`, {
      headers: { Authorization: `Bearer ${session.session.token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Falha ao carregar configurações.')
        return response.json()
      })
      .then((payload) => setDelayTolerance(payload.settings.delayToleranceMinutes))
      .catch((reason) => setError(reason instanceof Error ? reason.message : 'Falha ao carregar configurações.'))
      .finally(() => setLoading(false))
  }, [session.session.token])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (delayTolerance === null) return

    setSaving(true)
    setError(null)
    try {
      const response = await fetch(`${apiBaseUrl}/settings`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session.session.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ delayToleranceMinutes: delayTolerance }),
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok) throw new Error(payload?.message || 'Falha ao salvar configurações.')
      setDelayTolerance(payload.settings.delayToleranceMinutes)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 3000)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Falha ao salvar configurações.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-content-shell">
      <header className="admin-content-header">
        <div className="admin-content-header__copy">
          <h2>Configurações do Sistema — {session.organization.name}</h2>
          <p>Defina a tolerância de atraso aplicada às marcações.</p>
        </div>
      </header>

      {saved ? <div className="admin-toast admin-toast--success"><strong>Configuração salva com sucesso!</strong></div> : null}
      {error ? <div className="admin-toast admin-toast--error">{error}</div> : null}

      <div className="admin-panel" style={{ padding: '24px', backgroundColor: 'var(--surface)', maxWidth: '600px' }}>
        <form onSubmit={handleSave} style={{ display: 'grid', gap: '20px' }}>
          <div>
            <label htmlFor="delayTolerance" style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)', marginBottom: '6px' }}>
              Tolerância de atraso (minutos)
            </label>
            <input
              id="delayTolerance"
              type="number"
              min="0"
              max="180"
              value={delayTolerance ?? ''}
              onChange={(event) => setDelayTolerance(Number(event.target.value))}
              disabled={loading}
              required
              style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--line)', borderRadius: '6px', fontSize: '0.9rem', backgroundColor: 'var(--bg-alt)', color: 'var(--text)' }}
            />
            <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: 'var(--muted)' }}>
              {loading ? 'Carregando valor salvo...' : 'O valor é carregado e salvo pela API da organização.'}
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid var(--line)' }}>
            <button type="submit" disabled={loading || saving || delayTolerance === null} className="admin-action-button" style={{ backgroundColor: 'var(--brand-purple)', color: '#fff', padding: '10px 20px', borderRadius: '6px', fontWeight: 600 }}>
              {saving ? 'Salvando...' : 'Salvar tolerância'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
